import { formatDateOnlyLocal } from '@erp/shared-utils';
import type { BoardFreshnessRun } from '@erp/rpc';
import { isSameLocalDay } from '../components/boardFreshness';
import { syncedOnSnapshotDay } from './lineFacts';

/**
 * The Line footer's refresh stamp (GEN-3285): when the engine run that
 * produced the line ended, and when its stock was read. They are different
 * jobs at different times, so each says on its own whether it is from today.
 */
export interface LineRefreshStamp {
  run: {
    /** ISO timestamp: the run's end, or its start while it has none. */
    at: string | null;
    /** True when `at` is the run's end; false when it fell back to its start. */
    ended: boolean;
    current: boolean;
  };
  stock: {
    /** Snapshot date (`YYYY-MM-DD`). */
    asOf: string | null;
    /** When the feed wrote it, only when that was on the snapshot's day. */
    syncedAt: string | null;
    current: boolean;
  };
}

export function lineRefreshStamp(
  input: {
    engineRun: BoardFreshnessRun | null;
    asOf: string | null;
    asOfSyncedAt: string | null;
  },
  now: Date,
): LineRefreshStamp {
  const runAt = input.engineRun?.completedAt ?? input.engineRun?.startedAt;
  const { asOf, asOfSyncedAt } = input;
  return {
    run: {
      at: runAt ?? null,
      ended: input.engineRun?.completedAt != null,
      current: runAt !== undefined && isSameLocalDay(runAt, now),
    },
    stock: {
      asOf,
      // A late write of an earlier snapshot (GEN-3149) is still that day's
      // stock: show the date, not the write time.
      syncedAt:
        asOf !== null &&
        asOfSyncedAt !== null &&
        syncedOnSnapshotDay(asOf, asOfSyncedAt)
          ? asOfSyncedAt
          : null,
      current: asOf !== null && asOf === formatDateOnlyLocal(now),
    },
  };
}
